import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../lib/api";
import { useSeo } from "../components/site/Layout";

const toForm = (f) => ({ ...f, price_eur: (f.price_cents / 100).toString(), features: f.features.join("\n") });

const ForfaitEditor = ({ initial, onSaved, isNew }) => {
  const [f, setF] = useState(initial);
  const [error, setError] = useState("");
  const tid = isNew ? "new" : initial.slug;
  const save = async (e) => {
    e.preventDefault();
    const price = Number(String(f.price_eur).replace(",", "."));
    if (!price || price <= 0 || price > 50000) return setError("Le prix doit être supérieur à 0 € et ne pas dépasser 50 000 €.");
    setError("");
    const body = { name: f.name, description: f.description, features: f.features.split("\n"), price_eur: price, active: f.active, order: Number(f.order) || 0 };
    try {
      if (isNew) await adminApi.post("/forfaits", body); else await adminApi.put(`/forfaits/${f.id}`, body);
      toast.success("Forfait enregistré"); onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  const inp = (k, extra = {}) => ({ className: "field-input mt-2", value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), "data-testid": `forfait-${tid}-${k}`, ...extra });
  return (
    <form onSubmit={save} className="rounded-2xl bg-white border border-slate-200 p-6 grid md:grid-cols-2 gap-5" data-testid={`forfait-editor-${tid}`}>
      <label className="block md:col-span-2"><span className="font-semibold text-navy">Nom</span><input {...inp("name")} /></label>
      <label className="block md:col-span-2"><span className="font-semibold text-navy">Description</span><textarea rows={3} {...inp("description")} /></label>
      <label className="block"><span className="font-semibold text-navy">Prix TTC (€)</span><input inputMode="decimal" {...inp("price_eur")} /></label>
      <label className="block"><span className="font-semibold text-navy">Ordre d'affichage</span><input type="number" {...inp("order")} /></label>
      <label className="block md:col-span-2"><span className="font-semibold text-navy">Points clés (un par ligne)</span><textarea rows={4} {...inp("features")} /></label>
      <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="h-6 w-6 accent-[#2E5683]" data-testid={`forfait-${tid}-active`} /><span className="font-semibold text-navy">Actif (visible sur la page Tarifs)</span></label>
      <div className="md:col-span-2 flex items-center gap-4">
        <button type="submit" className="btn-primary" data-testid={`forfait-${tid}-save`}>{isNew ? "Créer le forfait" : "Enregistrer"}</button>
        {error && <p role="alert" className="text-red-700 font-medium" data-testid={`forfait-${tid}-error`}>{error}</p>}
      </div>
    </form>
  );
};

export default function AdminForfaits() {
  useSeo("Forfaits — admin");
  const [rows, setRows] = useState(null);
  const [k, setK] = useState(0);
  const load = useCallback(() => adminApi.get("/forfaits").then((r) => { setRows(r.data); setK((x) => x + 1); }), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-forfaits" className="max-w-4xl">
      <h1 className="text-3xl sm:text-4xl">Forfaits</h1>
      <p className="mt-2 text-slate-600">Les modifications sont immédiatement visibles sur la page Tarifs. Le montant facturé est toujours lu ici, côté serveur.</p>
      <div className="mt-6 space-y-6">
        {rows?.map((f) => <ForfaitEditor key={`${f.id}-${k}`} initial={toForm(f)} onSaved={load} />)}
        <h2 className="text-2xl pt-4">Nouveau forfait</h2>
        <ForfaitEditor key={`new-${k}`} isNew initial={{ name: "", description: "", price_eur: "", features: "", active: true, order: 10, slug: "new" }} onSaved={load} />
      </div>
    </div>
  );
}
